import type { EmailTemplate } from '@common/types/lawfirms';
import type { ProspectResponse } from '@common/types/prospects';
import { parseCurrency } from '@/utils';

type ProspectSummaryField = keyof ProspectResponse['prospectSummary'];

const CURRENCY_SUMMARY_FIELDS = new Set<ProspectSummaryField>([
    'estimatedDisbursements',
    'estimatedRegistrationFees',
    'estimatedTitleInsuranceFee',
    'landTransferTax',
    'landTransferTaxRebate',
    'legalFee',
    'legalFeeWithHst',
    'netLandTransferTax',
    'purchasePrice',
    'titleInsurancePrice',
    'provincialLandTransferTax',
    'municipalLandTransferTax',
    'provincialRebate',
    'municipalRebate',
    'disbursementWithHst',
    'registrationFeeWithHst',
    'titleInsuranceFeeWithHst',
    'legalFeeDiscount',
]);

const PROSPECT_SUMMARY_FIELDS = new Set<ProspectSummaryField>([
    'estimatedDisbursements',
    'estimatedRegistrationFees',
    'estimatedTitleInsuranceFee',
    'isFirstTimeHomeBuyer',
    'landTransferTax',
    'landTransferTaxRebate',
    'legalFee',
    'legalFeeWithHst',
    'netLandTransferTax',
    'isResaleOrNewBuild',
    'isPropertyInToronto',
    'purchasePrice',
    'isPurchaseRequireMortgage',
    'titleInsurancePrice',
    'isPropertyInCondo',
    'isTenantLivingInProperty',
    'isPropertyHaveRenter',
    'isMortgageUsedForConstruction',
    'provincialLandTransferTax',
    'municipalLandTransferTax',
    'provincialRebate',
    'municipalRebate',
    'disbursementWithHst',
    'registrationFeeWithHst',
    'titleInsuranceFeeWithHst',
    'legalFeeDiscount',
]);

function toProspectSummaryField(name: string): ProspectSummaryField | null {
    if (name === 'estimatedDisbursementsWithHst') {
        return 'disbursementWithHst';
    }
    if (PROSPECT_SUMMARY_FIELDS.has(name as ProspectSummaryField)) {
        return name as ProspectSummaryField;
    }
    return null;
}

export interface QuoteEmailData {
    emailTemplateId: number;
    emailTemplateType?: string | null;
    quoteTemplateId: number;
    isQuoteReminderOn: boolean;
    isQuoteApprovalEnable: boolean;
    emailSubject?: string | null;
    emailBodyIntroduction?: string | null;
    emailBodyClosing?: string | null;
    emailSignature?: string | null;
    totalNumberOfReminders?: number | null;
    frequencyOfReminder?: string | null;
    reminderTime?: string | null;
    emailCC?: string[] | null;
    disclaimerText?: string;
}

export function mergePatchResponse(
    prev: ProspectResponse,
    response: ProspectResponse,
    patchedField: string,
): ProspectResponse {
    const summaryField = toProspectSummaryField(patchedField);
    if (summaryField) {
        return {
            ...response,
            prospectSummary: {
                ...response.prospectSummary,
                [summaryField]: prev.prospectSummary[summaryField],
            },
        };
    }

    return {
        ...response,
        [patchedField]: prev[patchedField as keyof ProspectResponse],
    };
}

export function applyProspectFieldUpdate(
    prospect: ProspectResponse,
    name: string,
    value: unknown,
): ProspectResponse {
    const summaryField = toProspectSummaryField(name);
    if (summaryField) {
        return {
            ...prospect,
            prospectSummary: {
                ...prospect.prospectSummary,
                [summaryField]: value,
            },
        };
    }
    return {
        ...prospect,
        [name]: value,
    };
}

export function applyContactUpdate(
    prospect: ProspectResponse,
    key: string,
    value: unknown,
): ProspectResponse {
    return {
        ...prospect,
        contact: {
            ...prospect.contact,
            [key]: value,
        },
    };
}

export function mapEmailTemplateToQuoteEmailData(emailTemplate: EmailTemplate): QuoteEmailData {
    return {
        emailTemplateId: emailTemplate.emailTemplateId,
        emailTemplateType: emailTemplate.emailTemplateType,
        quoteTemplateId: emailTemplate.quoteTemplateId as number,
        isQuoteReminderOn: emailTemplate.isQuoteReminderOn,
        isQuoteApprovalEnable: emailTemplate.isQuoteApprovalEnable,
        emailSubject: emailTemplate.emailSubject,
        emailBodyIntroduction: emailTemplate.emailBodyIntroduction,
        emailBodyClosing: emailTemplate.emailBodyClosing,
        emailSignature: emailTemplate.emailSignature,
        totalNumberOfReminders: emailTemplate.totalNumberOfReminders,
        frequencyOfReminder: emailTemplate.frequencyOfReminder,
        reminderTime: emailTemplate.reminderTime,
        emailCC: emailTemplate.emailCC,
        disclaimerText: emailTemplate.disclaimerText ?? undefined,
    };
}

export function toPatchPath(name: string): string {
    if (name === 'estimatedDisbursementsWithHst') {
        return '/disbursementWithHst';
    }
    return `/${name}`;
}

export function toPatchValue(name: string, value: unknown): unknown {
    const summaryField = toProspectSummaryField(name);
    if (summaryField && CURRENCY_SUMMARY_FIELDS.has(summaryField)) {
        if (value == null || value === '') {
            return null;
        }
        const parsed = Number.parseFloat(parseCurrency(String(value)));
        return Number.isNaN(parsed) ? null : parsed;
    }
    return value ?? null;
}
